/* eslint-disable max-lines */
import { Shirt } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";

import {
  useArtwork,
  useEnhancedImageSource,
  type UploadArtworkParams
} from "@design-lab/entities/artwork";
import type { Side } from "@design-lab/shared";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useNotification } from "@design-lab/shared/ui/notification";
import { GradientButton } from "ai-image-generation/GradientButton";
import { DESIGN_LAB_ACCEPT_CONFIG, validateDesignLabArtworkFile } from "common/artwork-file-types";

import { MENU_IDS } from "../../model/state";
import { SHIRT_TAB_IDS } from "../../model/types/TabTypes";
import { useArtworkSelection } from "../../model/useArtworkSelection";
import { useMenuState } from "../../model/useMenuState";
import { CustomLoadingBar } from "./CustomLoadingBar";
import { ImageUploadedDisplay } from "./ImageUploadedDisplay";
import { UploadInterface } from "./UploadInterface";
import { AiDesignIcon } from "./icons";
import { BackShirtIcon } from "./icons/BackShirtIcon";

type UploadContentProps = {
  currentSide: Side;
  activeTab: string;
  uploadArtwork: (params: UploadArtworkParams) => Promise<void>;
  deleteArtwork: (placement: string, artworkId?: string) => Promise<void>;
};

export function UploadContent({
  currentSide,
  activeTab,
  uploadArtwork,
  deleteArtwork
}: UploadContentProps) {
  const { getMachineBySide } = useArtwork();
  const { error: notifyError } = useNotification();
  const { setSelection, clearSelection, getActiveSelection } = useArtworkSelection();
  const { handleMenuChange } = useMenuState();
  const machine = getMachineBySide(currentSide);
  const { artworkId, source } = useEnhancedImageSource({ side: currentSide });
  const [shouldOpenCanva, setShouldOpenCanva] = useState(false);
  const { isMobile } = useResponsive();

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(currentSide, artworkId);
    // Clear the selection when artwork is removed
    clearSelection(currentSide);
  }, [deleteArtwork, currentSide, artworkId, clearSelection]);

  const handleFileDrop = useCallback(
    async (droppedFiles: File[]) => {
      const file = droppedFiles[0];
      if (!file) return;

      const { valid, message } = await validateDesignLabArtworkFile(file);
      if (!valid) {
        notifyError({ title: "Upload Failed", description: message });
        return;
      }

      // Delete any existing artwork
      const activeSelection = getActiveSelection(currentSide);
      if (activeSelection.selectedId) {
        deleteArtwork(currentSide, activeSelection.selectedId?.toString());
      }
      setSelection(currentSide, MENU_IDS.UPLOAD_IMPORT, Number(artworkId));

      try {
        await uploadArtwork({ file, side: currentSide });
      } catch (error) {
        const errorData = (error as any)?.response?.data;
        const errorMessage =
          errorData?.validation_error ||
          errorData?.error ||
          "Failed to upload file. Please try again.";

        window.Honeybadger?.notify("UploadContent handleFileDrop error", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            component: "UploadContent",
            action: "handleFileDrop",
            side: currentSide
          }
        });

        notifyError({
          title: "Upload Failed",
          description: errorMessage
        });
      }
    },
    [
      uploadArtwork,
      currentSide,
      notifyError,
      artworkId,
      deleteArtwork,
      setSelection,
      getActiveSelection
    ]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    accept: DESIGN_LAB_ACCEPT_CONFIG,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: handleFileDrop
  });

  const handleLibraryClick = useCallback(() => {
    handleMenuChange(MENU_IDS.LIBRARY);
  }, [handleMenuChange]);

  const handleAiDesignClick = useCallback(() => {
    handleMenuChange(MENU_IDS.AI_DESIGN);
  }, [handleMenuChange]);

  const handleCanvaFileReady = useCallback(
    async (file: File) => {
      try {
        await uploadArtwork({ file, side: currentSide, source: "canva" });
      } catch (error) {
        const errorData = (error as any)?.response?.data;
        const errorMessage =
          errorData?.validation_error ||
          errorData?.error ||
          "Failed to upload file from Canva. Please try again.";

        window.Honeybadger?.notify("UploadContent handleCanvaFileReady error", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            component: "UploadContent",
            action: "handleCanvaFileReady",
            side: currentSide
          }
        });

        notifyError({
          title: "Upload Failed",
          description: errorMessage
        });
      }
    },
    [uploadArtwork, currentSide, notifyError]
  );

  // State checks for different upload phases
  const isFileUploaded = Boolean(machine.state.context.artworkId);
  const { file } = machine.state.context;

  const renderUploadContent = () => {
    // Handle uploaded file state
    const activeSelection = getActiveSelection(currentSide);
    const isFileImported =
      activeSelection.activeSource === MENU_IDS.UPLOAD_IMPORT || !activeSelection.activeSource;

    // Restore enhanced image after reload when processing is finished
    if (machine.state.matches("finished") && source.enhancedImage && isFileImported) {
      return (
        <ImageUploadedDisplay imageUrl={source.enhancedImage.base64} onDelete={onArtworkRemove} />
      );
    }

    if (isFileUploaded && file && isFileImported) {
      return (
        <ImageUploadedDisplay imageUrl={URL.createObjectURL(file)} onDelete={onArtworkRemove} />
      );
    }

    // Handle loading state
    if (machine.state.hasTag("loading") && isFileImported) {
      return <CustomLoadingBar startTime={machine.state.context.startTime ?? 0} />;
    }

    // Default upload interface
    return (
      <>
        {isMobile && (
          <div className="mb-2 flex w-full flex-col gap-2">
            <p className="text-center text-lg font-semibold leading-[28px] text-gray-900 md:text-xl md:leading-[30px]">
              Start Design with AI
            </p>
            <GradientButton
              data-testid="design-with-ai-button-design-lab"
              className="py-3 text-sm font-bold text-white"
              onClick={handleAiDesignClick}
            >
              <AiDesignIcon className="size-6" />
              Design with AI
            </GradientButton>
          </div>
        )}
        <UploadInterface
          shouldOpenCanva={shouldOpenCanva}
          onCanvaAutoOpenComplete={() => {
            setShouldOpenCanva(false);
          }}
          onDeviceClick={open}
          getInputProps={getInputProps}
          onLibraryClick={handleLibraryClick}
          onCanvaFileReady={handleCanvaFileReady}
        />
      </>
    );
  };

  return (
    <section
      {...getRootProps()}
      className={`flex size-full flex-1 flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-8 transition-all duration-200 ${
        isDragActive ? "border-blue-600 bg-blue-50 shadow-lg" : "border-blue-400 bg-transparent"
      }`}
    >
      {!isMobile && (
        <div className={`flex items-center gap-2 text-gray-900`}>
          {activeTab === SHIRT_TAB_IDS.BACK ? (
            <BackShirtIcon className="size-6 text-gray-900" />
          ) : (
            <Shirt className="size-6 text-gray-900" />
          )}
          <h2 className="text-xl font-semibold leading-[30px]">
            {activeTab === SHIRT_TAB_IDS.BACK ? "Back of Shirt" : "Front of Shirt"}
          </h2>
        </div>
      )}

      {renderUploadContent()}
    </section>
  );
}
